import { forwardRef, useImperativeHandle, useState } from "react";
import { Box, Space, Text, Textarea, Title, Grid } from "@mantine/core";
import { Controller, useForm } from "react-hook-form";
import { useMutation } from "@apollo/client";
import { Product, Order } from "../../../types/interfaces";
import { UPDATE_REMOVABLE_ITEM } from "../../../gql/items";
import {
  ImprintType,
  ItemType,
  RemovableItemOrderType,
} from "../../../types/enums";
import { Switch } from "../../../stories/Switch/Switch";

type PartialDentureTryInFeedbackFormData = {
  teethLengthMaxillary: string;
  teethShapeMaxillary: string;
  teethShadeMaxillary: string;
  missingTeethMaxillary: string;
  otherMaxillary: string;
  teethLengthMandibular: string;
  teethShapeMandibular: string;
  teethShadeMandibular: string;
  missingTeethMandibular: string;
  otherMandibular: string;
};

type PartialDentureTryInFeedbackProps = {
  product?: Product;
  previousProduct?: Product;
  order?: Order;
};

export interface PartialDentureTryInFeedbackRef {
  saveData: () => Promise<void>;
}

export const PartialDentureTryInFeedback = forwardRef<
  PartialDentureTryInFeedbackRef,
  PartialDentureTryInFeedbackProps
>(({ product, previousProduct, order }, ref) => {
  // Find RDV2 PAP product
  const rdv2Product = order?.products?.findLast(
    (p: Product) =>
      p.productType === ItemType.PARTIAL_DENTURE &&
      p.removableItem?.[0]?.imprintType === ImprintType.TRY_IN_RDV2
  );

  const newRemovableItem =
    rdv2Product?.removableItem?.[0] ?? product?.removableItem?.[0];

  const [wantModifications, setWantModifications] = useState(
    !!newRemovableItem?.dentistNotes
  );

  const previousRemovableItem = previousProduct?.removableItem?.[0];

  // Determine arches from previous product or current product
  const allPreviousTeeth = (previousProduct?.removableItem ?? []).flatMap(
    item => item.teeth ?? []
  );
  const allCurrentTeeth = (product?.removableItem ?? []).flatMap(
    item => item.teeth ?? []
  );
  const teeth =
    allPreviousTeeth.length > 0 ? allPreviousTeeth : allCurrentTeeth;
  const hasMaxillary = teeth.some((t: number) => t >= 11 && t <= 28);
  const hasMandibular = teeth.some((t: number) => t >= 31 && t <= 48);
  const isBimaxillary = hasMaxillary && hasMandibular;

  const isRequestingNewTryIn =
    newRemovableItem?.partialDentureOrderType === RemovableItemOrderType.TRY_IN;
  const showFeedbackForm = isRequestingNewTryIn || wantModifications;

  // Parse existing dentistNotes (same format as PAC)
  const parseDentistNotes = (notes: string | null | undefined) => {
    if (!notes) return {};
    const result: Partial<PartialDentureTryInFeedbackFormData> = {};
    const maxillaryMatch = notes.match(
      /\[Arcade maxillaire\]\n([\s\S]*?)(?=\[Arcade mandibulaire\]|$)/
    );
    const mandibularMatch = notes.match(/\[Arcade mandibulaire\]\n([\s\S]*?)$/);
    const parseSection = (section: string, isMaxillary: boolean) => {
      const prefix = isMaxillary ? "Maxillary" : "Mandibular";
      const teethLengthMatch = section.match(/Longueur des dents: (.+)/);
      const teethShapeMatch = section.match(/Forme des dents: (.+)/);
      const teethShadeMatch = section.match(/Teinte: (.+)/);
      const missingTeethMatch = section.match(
        /Dents manquantes ou incorrectes: (.+)/
      );
      const otherMatch = section.match(/Autre: (.+)/);
      if (teethLengthMatch)
        result[
          `teethLength${prefix}` as keyof PartialDentureTryInFeedbackFormData
        ] = teethLengthMatch[1];
      if (teethShapeMatch)
        result[
          `teethShape${prefix}` as keyof PartialDentureTryInFeedbackFormData
        ] = teethShapeMatch[1];
      if (teethShadeMatch)
        result[
          `teethShade${prefix}` as keyof PartialDentureTryInFeedbackFormData
        ] = teethShadeMatch[1];
      if (missingTeethMatch)
        result[
          `missingTeeth${prefix}` as keyof PartialDentureTryInFeedbackFormData
        ] = missingTeethMatch[1];
      if (otherMatch)
        result[`other${prefix}` as keyof PartialDentureTryInFeedbackFormData] =
          otherMatch[1];
    };
    if (maxillaryMatch) parseSection(maxillaryMatch[1], true);
    if (mandibularMatch) parseSection(mandibularMatch[1], false);
    return result;
  };

  const existingNotes = parseDentistNotes(newRemovableItem?.dentistNotes);

  const { control, watch } = useForm<PartialDentureTryInFeedbackFormData>({
    defaultValues: {
      teethLengthMaxillary: existingNotes.teethLengthMaxillary ?? "",
      teethShapeMaxillary: existingNotes.teethShapeMaxillary ?? "",
      teethShadeMaxillary: existingNotes.teethShadeMaxillary ?? "",
      missingTeethMaxillary: existingNotes.missingTeethMaxillary ?? "",
      otherMaxillary: existingNotes.otherMaxillary ?? "",
      teethLengthMandibular: existingNotes.teethLengthMandibular ?? "",
      teethShapeMandibular: existingNotes.teethShapeMandibular ?? "",
      teethShadeMandibular: existingNotes.teethShadeMandibular ?? "",
      missingTeethMandibular: existingNotes.missingTeethMandibular ?? "",
      otherMandibular: existingNotes.otherMandibular ?? "",
    },
  });

  const [updateRemovableItem] = useMutation(UPDATE_REMOVABLE_ITEM, {
    refetchQueries: ["getOrderByUniqueAttribute"],
    awaitRefetchQueries: true,
  });

  const teethLengthMaxillary = watch("teethLengthMaxillary");
  const teethShapeMaxillary = watch("teethShapeMaxillary");
  const teethShadeMaxillary = watch("teethShadeMaxillary");
  const missingTeethMaxillary = watch("missingTeethMaxillary");
  const otherMaxillary = watch("otherMaxillary");
  const teethLengthMandibular = watch("teethLengthMandibular");
  const teethShapeMandibular = watch("teethShapeMandibular");
  const teethShadeMandibular = watch("teethShadeMandibular");
  const missingTeethMandibular = watch("missingTeethMandibular");
  const otherMandibular = watch("otherMandibular");

  const buildDentistNotes = (): string => {
    // same as PAC TryInFeedback
    const notes: string[] = [];
    if (hasMaxillary) {
      const maxillaryNotes: string[] = [];
      if (teethLengthMaxillary?.trim())
        maxillaryNotes.push(
          `Longueur des dents: ${teethLengthMaxillary.trim()}`
        );
      if (teethShapeMaxillary?.trim())
        maxillaryNotes.push(`Forme des dents: ${teethShapeMaxillary.trim()}`);
      if (teethShadeMaxillary?.trim())
        maxillaryNotes.push(`Teinte: ${teethShadeMaxillary.trim()}`);
      if (missingTeethMaxillary?.trim())
        maxillaryNotes.push(
          `Dents manquantes ou incorrectes: ${missingTeethMaxillary.trim()}`
        );
      if (otherMaxillary?.trim())
        maxillaryNotes.push(`Autre: ${otherMaxillary.trim()}`);
      if (maxillaryNotes.length > 0)
        notes.push(`[Arcade maxillaire]\n${maxillaryNotes.join("\n")}`);
    }
    if (hasMandibular) {
      const mandibularNotes: string[] = [];
      if (teethLengthMandibular?.trim())
        mandibularNotes.push(
          `Longueur des dents: ${teethLengthMandibular.trim()}`
        );
      if (teethShapeMandibular?.trim())
        mandibularNotes.push(`Forme des dents: ${teethShapeMandibular.trim()}`);
      if (teethShadeMandibular?.trim())
        mandibularNotes.push(`Teinte: ${teethShadeMandibular.trim()}`);
      if (missingTeethMandibular?.trim())
        mandibularNotes.push(
          `Dents manquantes ou incorrectes: ${missingTeethMandibular.trim()}`
        );
      if (otherMandibular?.trim())
        mandibularNotes.push(`Autre: ${otherMandibular.trim()}`);
      if (mandibularNotes.length > 0)
        notes.push(`[Arcade mandibulaire]\n${mandibularNotes.join("\n")}`);
    }
    return notes.join("\n\n");
  };

  const saveData = async (): Promise<void> => {
    if (!isRequestingNewTryIn && !wantModifications) return;

    const dentistNotes = buildDentistNotes();
    if (!dentistNotes) return;

    try {
      // update of all rdv2 removableItems of the product
      const targetRemovableItems =
        rdv2Product?.removableItem ?? product?.removableItem ?? [];
      await Promise.all(
        targetRemovableItems.map(item =>
          updateRemovableItem({
            variables: {
              where: { id: item.id },
              data: {
                dentistNotes: { set: dentistNotes },
              },
            },
          })
        )
      );
    } catch (error) {
      console.error("Error in PartialDentureTryInFeedback saveData:", error);
    }
  };

  useImperativeHandle(ref, () => ({ saveData }));

  const renderArchFeedbackForm = (
    archType: "maxillary" | "mandibular",
    title: string
  ) => (
    <Box>
      <Title order={4}>{title}</Title>
      <Space h="sm" />
      <Text size="sm" fw={500} mb="xs">
        Longueur des dents
      </Text>
      <Controller
        name={
          archType === "maxillary"
            ? "teethLengthMaxillary"
            : "teethLengthMandibular"
        }
        control={control}
        render={({ field }) => (
          <Textarea
            {...field}
            placeholder="Décrivez les changements souhaités..."
            minRows={1}
            autosize
            maxRows={2}
          />
        )}
      />
      <Space h="xs" />
      <Text size="sm" fw={500} mb="xs">
        Forme des dents
      </Text>
      <Controller
        name={
          archType === "maxillary"
            ? "teethShapeMaxillary"
            : "teethShapeMandibular"
        }
        control={control}
        render={({ field }) => (
          <Textarea
            {...field}
            placeholder="Décrivez les changements souhaités..."
            minRows={1}
            autosize
            maxRows={2}
          />
        )}
      />
      <Space h="xs" />
      <Text size="sm" fw={500} mb="xs">
        Teinte
      </Text>
      <Controller
        name={
          archType === "maxillary"
            ? "teethShadeMaxillary"
            : "teethShadeMandibular"
        }
        control={control}
        render={({ field }) => (
          <Textarea
            {...field}
            placeholder="Décrivez les changements souhaités..."
            minRows={1}
            autosize
            maxRows={2}
          />
        )}
      />
      <Space h="xs" />
      <Text size="sm" fw={500} mb="xs">
        Dents manquantes ou incorrectes
      </Text>
      <Controller
        name={
          archType === "maxillary"
            ? "missingTeethMaxillary"
            : "missingTeethMandibular"
        }
        control={control}
        render={({ field }) => (
          <Textarea
            {...field}
            placeholder="Décrivez les changements souhaités..."
            minRows={1}
            autosize
            maxRows={2}
          />
        )}
      />
      <Space h="xs" />
      <Text size="sm" fw={500} mb="xs">
        Autre
      </Text>
      <Controller
        name={archType === "maxillary" ? "otherMaxillary" : "otherMandibular"}
        control={control}
        render={({ field }) => (
          <Textarea
            {...field}
            placeholder="Autres remarques..."
            minRows={1}
            autosize
            maxRows={2}
          />
        )}
      />
    </Box>
  );

  if (!hasMaxillary && !hasMandibular) {
    return (
      <Box>
        <Text>Aucune arcade détectée pour le feedback</Text>
      </Box>
    );
  }

  return (
    <Box>
      <Title order={3}>Remarques sur l'essayage</Title>
      <Space h="lg" />
      {!isRequestingNewTryIn && (
        <>
          <Switch
            label="Souhaitez-vous apporter des modifications ?"
            checked={wantModifications}
            onChange={setWantModifications}
            size="md"
          />
          <Space h="lg" />
        </>
      )}
      {showFeedbackForm && (
        <>
          <Title order={3}>Que faut-il changer dans l'essayage</Title>
          <Space h="lg" />
          {isBimaxillary ? (
            <Grid gap="xl">
              <Grid.Col span={6}>
                {hasMaxillary &&
                  renderArchFeedbackForm("maxillary", "Arcade maxillaire")}
              </Grid.Col>
              <Grid.Col span={6}>
                {hasMandibular &&
                  renderArchFeedbackForm("mandibular", "Arcade mandibulaire")}
              </Grid.Col>
            </Grid>
          ) : (
            <>
              {hasMaxillary &&
                renderArchFeedbackForm(
                  "maxillary",
                  "Que faut-il changer dans l'essayage de l'arcade maxillaire ?"
                )}
              {hasMandibular &&
                renderArchFeedbackForm(
                  "mandibular",
                  "Que faut-il changer dans l'essayage de l'arcade mandibulaire ?"
                )}
            </>
          )}
        </>
      )}
      <Space h="xl" />
    </Box>
  );
});

PartialDentureTryInFeedback.displayName = "PartialDentureTryInFeedback";
